import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Form, Input, message, Modal, Button, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { deleteBankImage, getBank } from '../../store/actions/bank';
import config from '../../config';

// Helper to resolve image URL
const resolveImageUrl = (path) => {
  if (!path) return '';

  // 1. Handle replacement of production hostname for local development
  if (path.startsWith('http')) {
    const oldBase = "https://adminapi.zeronine.ai";
    const newBase = config.API_URL.replace(/\/api\/?$/, '');

    // Replace production domain with local IP address if we're on local API
    if (path.startsWith(oldBase) && newBase.includes('192.168')) {
      return path.replace(oldBase, newBase);
    }
    // If it's already a full URL (including replaced ones), just return it
    return path;
  }

  // 2. Handle relative paths
  const baseUrl = config.API_URL.replace(/\/api\/?$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const BankForm = ({ visible, onCreate, onCancel, record }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [hasExistingImage, setHasExistingImage] = useState(false);

  // Reset or initialize form when visibility or record changes
  useEffect(() => {
    if (visible) {
      if (record) {
        const hasLogo = !!record.logo;
        setHasExistingImage(hasLogo);

        const initialUrl = resolveImageUrl(record.logo);

        form.setFieldsValue({
          name: record.name,
          file: hasLogo ? [{
            uid: '-1',
            name: 'current_logo.png',
            status: 'done',
            url: initialUrl,
            thumbUrl: initialUrl,
          }] : [],
        });
      } else {
        setHasExistingImage(false);
        form.resetFields();
      }
    }
  }, [record, form, visible]);

  // Utility to handle file list normalization
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList || [];
  };

  // Memoized upload configuration
  const uploadProps = useMemo(() => ({
    beforeUpload: (file) => {
      const isPNG = file.type === 'image/png';
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg';

      if (!isPNG && !isJPG) {
        message.error(`${file.name} is not a valid format. Please upload PNG/JPG only.`);
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      if (file.uid === '-1' && record?.id) {
        return new Promise((resolve) => {
          Swal.fire({
            title: 'Delete Logo?',
            text: 'Are you sure you want to delete this bank logo? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
          }).then(async (result) => {
            if (result.isConfirmed) {
              try {
                const success = await dispatch(deleteBankImage(record.id));
                if (success) {
                  setHasExistingImage(false);
                  await dispatch(getBank());
                  resolve(true);
                } else {
                  resolve(false);
                }
              } catch (error) {
                resolve(false);
              }
            } else {
              resolve(false);
            }
          });
        });
      }
      return true;
    },
    onChange: (info) => {
      if (info.fileList.length > 0 && info.fileList[0].originFileObj) {
        setHasExistingImage(false);
      }
    },
    maxCount: 1,
    listType: "picture",
    accept: ".png,.jpg,.jpeg"
  }), [dispatch, record, getBank]);

  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields();
      await onCreate(values);
    } catch (error) {
      console.warn('Form validation failed:', error);
    }
  }, [form, onCreate]);

  return (
    <Modal
      open={visible}
      title={record ? "Update Bank" : "Add Bank"}
      okText={record ? "Update" : "Submit"}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={handleOk}
      width={600}
      destroyOnClose
      maskClosable={false}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        name="bank_form"
        preserve={false}
      >
        <Form.Item
          name="name"
          label="Bank Name"
          rules={[
            { required: true, message: 'Please enter the bank name' },
            { whitespace: true, message: 'Bank name cannot be empty' },
            { min: 2, message: 'Bank name must be at least 2 characters long' }
          ]}
        >
          <Input placeholder="Enter full bank name" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="file"
          label="Bank Logo"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          extra="Supported formats: PNG, JPG, JPEG (Max size: 2MB)"
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} style={{ width: '100%' }}>
              Click to Upload Logo
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BankForm;